import { useState } from "react";
import {
  clearCompanyData,
  downloadCompanyBackup,
  restoreCompanyBackup,
  type CompanyBackup,
} from "./api";
import { useAuth } from "../auth/useAuth";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";

export function BackupPanel() {
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();
  const { session } = useAuth();
  // Clearing data is intentionally available only for the demo tenant (also
  // enforced server-side) so a real company can't wipe itself.
  const isDemo = session?.companyCode === "DEMO";

  const [downloading, setDownloading] = useState(false);
  const [restoreFile, setRestoreFile] = useState<CompanyBackup | null>(null);
  const [restoreFileName, setRestoreFileName] = useState<string>("");
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      const backup = await downloadCompanyBackup();
      const blob = new Blob([JSON.stringify(backup, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const stamp = new Date().toISOString().slice(0, 10);
      const link = document.createElement("a");
      link.href = url;
      link.download = `vms-backup-${backup.company_code}-${stamp}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      showSuccess("Backup downloaded");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to download backup");
    } finally {
      setDownloading(false);
    }
  }

  function onFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setRestoreFile(null);
    setRestoreFileName("");
    setParseError(null);
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result)) as CompanyBackup;
        if (typeof parsed.vms_backup_version !== "number" || !parsed.tables) {
          throw new Error("This file doesn't look like a VMS backup.");
        }
        setRestoreFile(parsed);
        setRestoreFileName(file.name);
      } catch (error) {
        setParseError(error instanceof Error ? error.message : "Could not read backup file");
      }
    };
    reader.readAsText(file);
  }

  async function handleRestore() {
    if (!restoreFile) return;
    if (confirmText !== "RESTORE") {
      showError('Type RESTORE (in capitals) to confirm.');
      return;
    }
    const counts = Object.entries(restoreFile.tables)
      .map(([table, rows]) => `${rows.length} ${table}`)
      .join(", ");
    const ok = await confirm({
      title: "Replace ALL current data?",
      message: `This wipes your company's current visitors, visits, logs, hosts, sites and config, and loads the backup (${counts}). A safety snapshot of the current data is saved first, but this cannot be undone from the app.`,
      confirmLabel: "Replace everything",
      danger: true,
    });
    if (!ok) return;

    setRestoring(true);
    try {
      const result = await restoreCompanyBackup(restoreFile);
      const summary = Object.entries(result.restored)
        .filter(([, n]) => n > 0)
        .map(([table, n]) => `${n} ${table}`)
        .join(", ");
      showSuccess(`Restored: ${summary || "nothing"}. Safety snapshot saved on the server.`);
      setRestoreFile(null);
      setRestoreFileName("");
      setConfirmText("");
    } catch (error) {
      showError(error instanceof Error ? error.message : "Restore failed");
    } finally {
      setRestoring(false);
    }
  }

  async function handleClear() {
    const ok = await confirm({
      title: "Clear all demo visitor data?",
      message:
        "This permanently deletes the demo company's visitors, visits and access logs (hosts, sites, users and settings are kept). A safety snapshot is saved on the server first. Continue?",
      confirmLabel: "Clear demo data",
      danger: true,
    });
    if (!ok) return;

    setClearing(true);
    try {
      const result = await clearCompanyData();
      const summary = Object.entries(result.cleared)
        .filter(([, n]) => n > 0)
        .map(([table, n]) => `${n} ${table}`)
        .join(", ");
      showSuccess(`Cleared: ${summary || "nothing"}. Safety snapshot saved on the server.`);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Failed to clear data");
    } finally {
      setClearing(false);
    }
  }

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Backup &amp; Restore</h2>
      </header>
      <p className="management-note">
        Download a snapshot of this company's data — sites, hosts, visitors, visits, access logs and
        configuration — as a JSON file you can keep safe. Login accounts and SMTP passwords are not
        included. Uploaded photos/ID images live on the server and aren't in this file, but a restore
        never deletes them.
      </p>

      <div className="action-card">
        <button className="refresh-btn action-submit" type="button" onClick={() => void handleDownload()} disabled={downloading}>
          {downloading ? "Preparing..." : "Download Backup"}
        </button>
      </div>

      <div className="action-card">
        <h3 style={{ margin: "0 0 0.5rem" }}>Restore from a backup</h3>
        <p className="management-note" style={{ marginTop: 0 }}>
          Restoring <strong>replaces all current data</strong> with the contents of the backup file.
          The current data is snapshotted on the server first as a safety net.
        </p>
        <input type="file" accept="application/json,.json" onChange={onFileChange} aria-label="Backup file" />
        {parseError ? <p className="error-banner action-error">{parseError}</p> : null}
        {restoreFile ? (
          <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <span className="management-note" style={{ margin: 0 }}>
              Loaded <strong>{restoreFileName}</strong> — backup of{" "}
              <strong>{restoreFile.company_code}</strong> taken {restoreFile.created_at?.slice(0, 19).replace("T", " ")}.
            </span>
            <input
              className="search-input"
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              placeholder="Type RESTORE to enable"
              aria-label="Type RESTORE to confirm"
            />
            <button
              className="refresh-btn danger-action-btn"
              type="button"
              onClick={() => void handleRestore()}
              disabled={restoring || confirmText !== "RESTORE"}
            >
              {restoring ? "Restoring..." : "Restore (replace all data)"}
            </button>
          </div>
        ) : null}
      </div>

      {isDemo ? (
        <div className="action-card">
          <h3 style={{ margin: "0 0 0.5rem" }}>Clear demo data</h3>
          <p className="management-note" style={{ marginTop: 0 }}>
            Reset this demo by deleting all visitors, visits and access logs. Hosts, sites, users
            and settings are kept so the demo stays usable. A safety snapshot is saved first. This
            option is only available for the DEMO company.
          </p>
          <button
            className="refresh-btn danger-action-btn"
            type="button"
            onClick={() => void handleClear()}
            disabled={clearing}
          >
            {clearing ? "Clearing..." : "Clear demo data"}
          </button>
        </div>
      ) : null}
    </article>
  );
}
